<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import type { DrawerVisibilityParams } from '@/@core/utils/drawerVisibility'
import { useAssetStore } from '@/@vxapp/Assets/useAssetStore'
import AssetDrawer from '@/components/Drawers/Assets/AssetDrawer.vue'
import AssetListToolbar from '@/components/ServiceLists/Assets/AssetListToolbar.vue'
import AssetsDatatable from '@/components/Tables/Assets/AssetsDatatable.vue'

type AssetData = Record<string, unknown>
type ManagedQueryKey = 'page' | 'limit' | 'sort' | 'search'

definePage({ meta: { ...PageMeta.Assets.list.meta, pageTitle: PageMeta.Assets.list.title } })

const log = createLogger('AssetsListPage')
const route = useRoute()
const router = useRouter()
const assetStore = useAssetStore() as any

const assetsDataTable = useTemplateRef<InstanceType<typeof AssetsDatatable>>('assetsDataTable')

const assetData = ref<AssetData | null>(null)
const isAssetDrawerOpen = shallowRef(false)
const isRouteQueryReady = shallowRef(false)
const resultCount = shallowRef<number | null>(null)

const defaultQueryParams: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '+id' }
const managedQueryKeys: ManagedQueryKey[] = ['page', 'limit', 'sort', 'search']

const queryParams = computed<PaginationQueryParams>(() =>
  normalizeQueryParams(assetStore.queryParams ?? defaultQueryParams)
)

const normalizeQueryValue = (value: unknown): string | undefined => {
  const queryValue = Array.isArray(value) ? value[0] : value

  if (typeof queryValue === 'number') return String(queryValue)

  return typeof queryValue === 'string' ? queryValue : undefined
}

const normalizePositiveInteger = (value: unknown, fallback: number) => {
  const normalizedValue = normalizeQueryValue(value)
  const parsedValue = normalizedValue ? Number.parseInt(normalizedValue, 10) : Number.NaN

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

const normalizeQueryParams = (source: Record<string, unknown>): PaginationQueryParams => {
  const rawLimit = normalizeQueryValue(source.limit)
  const parsedLimit = rawLimit ? Number.parseInt(rawLimit, 10) : Number.NaN
  const search = normalizeQueryValue(source.search)?.trim()
  const sort = normalizeQueryValue(source.sort)

  return {
    ...defaultQueryParams,
    page: normalizePositiveInteger(source.page, defaultQueryParams.page),
    limit:
      parsedLimit === -1
        ? parsedLimit
        : normalizePositiveInteger(source.limit, defaultQueryParams.limit),
    sort: sort === '+id' || sort === '-id' ? sort : defaultQueryParams.sort,
    search: search || undefined,
  }
}

const queryParamsMatch = (left: PaginationQueryParams, right: PaginationQueryParams) =>
  left.page === right.page &&
  left.limit === right.limit &&
  left.sort === right.sort &&
  left.search === right.search

const applyRouteQueryParams = () => {
  const currentQueryParams = normalizeQueryParams(assetStore.queryParams ?? defaultQueryParams)
  const routeQueryParams = normalizeQueryParams(route.query as Record<string, unknown>)

  if (queryParamsMatch(currentQueryParams, routeQueryParams)) return false

  assetStore.queryParams = { ...assetStore.queryParams, ...routeQueryParams }

  return true
}

const buildRouteQuery = (
  source: PaginationQueryParams = normalizeQueryParams(assetStore.queryParams ?? defaultQueryParams)
) => {
  const nextQuery = { ...route.query } as Record<string, unknown>

  for (const key of managedQueryKeys) delete nextQuery[key]

  const queryParams = normalizeQueryParams(source as unknown as Record<string, unknown>)

  return {
    ...nextQuery,
    ...(queryParams.page !== defaultQueryParams.page ? { page: String(queryParams.page) } : {}),
    ...(queryParams.limit !== defaultQueryParams.limit ? { limit: String(queryParams.limit) } : {}),
    ...(queryParams.sort !== defaultQueryParams.sort ? { sort: queryParams.sort } : {}),
    ...(queryParams.search ? { search: queryParams.search } : {}),
  }
}

const routeQueryMatches = (left: Record<string, unknown>, right: Record<string, unknown>) =>
  JSON.stringify(left) === JSON.stringify(right)

const syncRouteQuery = async (queryParams?: PaginationQueryParams) => {
  const nextQuery = buildRouteQuery(queryParams)

  if (routeQueryMatches(route.query as Record<string, unknown>, nextQuery)) return

  await router.replace({ query: nextQuery })
}

const fetchAssets = async () => {
  await assetsDataTable.value?.fetchAssets()
}

const updateQueryParams = async (patch: Partial<PaginationQueryParams>) => {
  const nextQueryParams = normalizeQueryParams({
    ...assetStore.queryParams,
    ...patch,
  })

  assetStore.queryParams = nextQueryParams
  await syncRouteQuery(nextQueryParams)
  await nextTick()
  await fetchAssets()
}

const handleSearch = async (search: string) => {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

const handleLimitChange = async (limit: number) => {
  await updateQueryParams({ limit, page: 1 })
}

const handleSortChange = async (sort: string) => {
  await updateQueryParams({ sort, page: 1 })
}

const handleClear = async () => {
  assetStore.queryParams = { ...defaultQueryParams }
  await syncRouteQuery(defaultQueryParams)
  await nextTick()
  await fetchAssets()
}

const handlePageChange = async (page: number) => {
  await updateQueryParams({ page })
}

const handleLoaded = (entriesFound: number) => {
  resultCount.value = entriesFound
}

const handleDrawerVisibilityUpdate = (params: DrawerVisibilityParams) => {
  if (params.dialogName === 'AssetDrawer') isAssetDrawerOpen.value = params.visibility
}

const handleCreateAsset = async () => {
  try {
    const { responseCode } = await assetStore.fetchTemplate()

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load asset template', { responseCode })

      return
    }

    assetData.value = cloneReactiveData(assetStore.template ?? {})
    await nextTick()
    isAssetDrawerOpen.value = true
  } catch (error) {
    log.error('Failed to load asset template', error)
  }
}

const handleAssetCreate = async (params: AssetData) => {
  try {
    const { asset, responseCode } = await assetStore.create(params, false)

    if (responseCode !== ResponseCode.CREATED) {
      log.error('Failed to create asset', { responseCode })

      return
    }

    const assetGUID = asset?.assetGUID
    if (typeof assetGUID !== 'string') {
      log.error('Created asset did not include an assetGUID')

      return
    }

    await router.replace({
      name: PageMeta.Assets.preview.name as 'apps-assets-preview-guid',
      params: { guid: assetGUID },
    })
  } catch (error) {
    log.error('Failed to create asset', error)
  }
}

assetStore.queryParams = { ...defaultQueryParams }
applyRouteQueryParams()
isRouteQueryReady.value = true

onMounted(fetchAssets)

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value || !applyRouteQueryParams()) return

    await fetchAssets()
  },
  { deep: true }
)
</script>

<template>
  <section
    class="assets-workspace"
    data-testid="assets-workspace"
  >
    <VCard class="assets-workspace__hero">
      <VCardItem>
        <div class="assets-workspace__hero-content">
          <div>
            <span class="assets-workspace__eyebrow">Fleet operations</span>
            <VCardTitle class="assets-workspace__title">Assets</VCardTitle>
            <VCardSubtitle class="assets-workspace__subtitle">
              Inspect equipment identity, classification, condition context, and fleet records.
            </VCardSubtitle>
          </div>
          <div class="assets-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount === null ? '—' : resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>

      <VDivider />

      <VCardActions class="assets-workspace__actions">
        <VBtn
          color="primary"
          data-testid="new-asset-action"
          prepend-icon="tabler-plus"
          @click="handleCreateAsset"
        >
          New asset
        </VBtn>
        <VBtn
          data-testid="hero-refresh-assets"
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="fetchAssets"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>

    <AssetListToolbar
      :query-params="queryParams"
      :result-count="resultCount"
      :is-refreshing="assetStore.isLoading"
      @search="handleSearch"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClear"
      @refresh="fetchAssets"
    />

    <AssetsDatatable
      ref="assetsDataTable"
      @loaded="handleLoaded"
      @page-change="handlePageChange"
    />
  </section>

  <AssetDrawer
    v-if="assetData"
    :is-drawer-open="isAssetDrawerOpen"
    :asset-data="assetData"
    title="Create new Asset"
    @update="handleAssetCreate"
    @update:is-drawer-open="handleDrawerVisibilityUpdate"
  />
</template>

<style scoped lang="scss">
.assets-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.assets-workspace__hero {
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.assets-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.assets-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.assets-workspace__title {
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
}

.assets-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.assets-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.assets-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.assets-workspace__metric strong {
  font-size: 1.25rem;
  font-weight: 590;
}

.assets-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

@media (max-width: 599px) {
  .assets-workspace {
    gap: 1rem;
  }

  .assets-workspace__hero-content {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .assets-workspace__metric {
    min-inline-size: 0;
  }

  .assets-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .assets-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }
}
</style>
